import React from 'react';
import { useDispatch } from 'react-redux';
import { UnknownAction } from 'redux';
import styled from '@emotion/styled';

import { getProductImage } from '../../product/utils';
import { colors, Divider, Icon } from '../../../design-system';
import { CrossSell } from '../../product/components/CrossSell';
import {
  DATE_OF_SEND,
  MSG_FEEDBACK_SUCCESS,
  MSG_RETURN_TO_BASKET,
  RECEIVER_EMAIL,
} from '../locale';
import { PromotionLabel } from '../../catalog/components/PromotionLabel';
import paths from '../../routing/paths';
import { breakpoints, durations, opacities } from '../../../design-system/systemprovider';
import { LBL_COLOR, LBL_QUANTITY, LBL_SIZE } from '../../product/locale';
import { formatPrice } from '../../common/utils';
import { ERESA_CONTINUE_TO_BUY } from '../../ecart/locale';
import { TXT_LINK_CART } from '../../cart/locale';
import { loadCart, setShowFeedback } from '../../cart/actions';
import { Link, useLocation } from 'react-router-dom';
import { closeModalNew } from '../../common/actions';

type Props = {
  productRef: string;
  colorRef: string;
  productName: string;
  collectionName: string;
  onClose?: () => void;
  storePrice: number;
  originalPrice: number;
  size: string;
  colorLabel: string;
  promotionLabel?: string;
  promotionPercentage?: string;
  promotionBackgroundColor?: string;
  promotionTextColor?: string;
  isOutlet?: boolean;
  objectID: string;
  email?: string;
  date?: string;
  priceGiftCard?: number;
  isGiftCard?: boolean;
  showFeedback: boolean;
};

const PRODUCT_IMAGE_WIDTH = 104;
const PRODUCT_IMAGE_WIDTH_MOBILE = 68;
const PRODUCT_IMAGE_MAX_HEIGHT = 139;

const StyledFeedBackContainer = styled.div<{
  isGiftCard: boolean;
}>`
  padding: 8px 16px 16px 16px;

  @media (min-width: ${breakpoints.S}px) {
    padding: 16px;
  }

  .feed-back-header-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    @media (min-width: ${breakpoints.S}px) {
      margin-bottom: 24px;
    }
  }

  .mobile-icon-close {
    display: block;
    background: unset;
    padding: 0;
    border: none;
    height: 44px;
    width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-right: -14px;

    @media (min-width: ${breakpoints.S}px) {
      display: none;
    }
  }

  .feed-back-icon-message-block {
    display: flex;
    align-items: center;
    gap: 16px;

    p {
      margin: 0;
      font-size: 1.6rem;
      font-weight: 700;
      color: ${colors.BLACK};
    }
  }

  .feed-back-product-description-block {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 16px;

    img {
      width: ${PRODUCT_IMAGE_WIDTH_MOBILE}px;
      border-radius: 8px;

      @media (min-width: ${breakpoints.S}px) {
        width: ${PRODUCT_IMAGE_WIDTH}px;
        max-height: ${PRODUCT_IMAGE_MAX_HEIGHT}px;
      }
    }
  }

  .feed-back-product-info-block {
    display: grid;
    width: 100%;
    gap: 8px;
  }

  .feed-back-product-name-price-block {
    display: grid;
    gap: 4px;

    @media (min-width: ${breakpoints.S}px) {
      gap: 8px;
    }

    .feed-back-category-name {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: calc(100vw - ${PRODUCT_IMAGE_WIDTH_MOBILE + 48}px);

      @media (min-width: ${breakpoints.S}px) {
        width: calc(95vw - ${PRODUCT_IMAGE_WIDTH + 48}px);
      }

      @media (min-width: ${breakpoints.M}px) {
        width: calc(770px - ${PRODUCT_IMAGE_WIDTH + 48}px);
      }

      p {
        margin: 0;
      }

      .feed-back-category-price {
        font-size: 1.6rem;
        font-weight: 700;
      }

      .feed-back-name {
        font-size: 1.4rem;
        color: ${colors.MAIN_GREY};
        text-transform: capitalize;
      }

      .feed-back-category-price,
      .feed-back-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 70%;

        @media (min-width: ${breakpoints.S}px) {
          max-width: 80%;
        }
      }

      .feed-back-store-price {
        font-size: 1.6rem;
        font-weight: 700;
        color: ${colors.BLACK};
      }

      .feed-back-original-price {
        font-size: 1.6rem;
        color: ${colors.GREY2};
        text-decoration: line-through;
      }
    }
  }

  .feed-back-product-details-block {
    display: flex;
    align-items: ${(props) => (props.isGiftCard ? 'left' : 'initial')};
    flex-direction: ${(props) => (props.isGiftCard ? 'column' : 'row')};
    gap: ${(props) => (props.isGiftCard ? '4px' : '16px')};

    p,
    span {
      margin: 0;
      font-size: 1.2rem;
      color: ${colors.MAIN_GREY};

      @media (min-width: ${breakpoints.S}px) {
        font-size: 1.4rem;
      }
    }
    p {
      font-weight: 400;
    }
    span {
      font-weight: 700;
    }
  }

  .feed-back-color-size-quantity-block {
    display: flex;
    flex-direction: ${(props) => (props.isGiftCard ? 'column' : 'row')};
    gap: 4px;
  }

  .feed-back-color-size-quantity-block-gift-card {
    display: flex;
    flex-direction: row;
    gap: 4px;
  }

  .feed-back-cross-sell-block {
    margin: 0 -16px 12px -16px;

    @media (min-width: ${breakpoints.S}px) {
      margin: 0 -16px 12px -16px;
    }
  }

  .feed-back-buttons-block {
    margin-top: ${(props) => (props.isGiftCard ? '16px' : 0)};
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;

    @media (min-width: ${breakpoints.S}px) {
      flex-direction: row;
    }

    p {
      font-size: 1.4rem;
      font-weight: 500;
      margin: 0;
    }
  }

  .feed-back-close-button,
  .feed-back-cart-page-close-button {
    background-color: ${colors.WHITE};
    color: ${colors.BLACK};
    border: solid 1px ${colors.BLACK};
    cursor: pointer;
  }

  .feed-back-cart-page-close-button {
    height: 40px;
    width: 100%;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;

    @media (hover: hover) {
      &:hover {
        opacity: ${opacities.HOVERED};
      }
    }
  }

  .feed-back-modal-link-to-cart-anchor {
    text-decoration: none;
    background-color: ${colors.BLACK};
    color: ${colors.WHITE};
  }

  .feed-back-close-button,
  .feed-back-modal-link-to-cart-anchor {
    height: 40px;
    width: 100%;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity ${durations.FOCUS_DELAY}ms ease-in-out;

    @media (hover: hover) {
      &:hover {
        opacity: ${opacities.HOVERED};
      }
    }
  }
`;

export const FeedbackModal = ({
  productRef,
  colorRef,
  productName,
  collectionName,
  onClose,
  storePrice,
  originalPrice,
  size,
  colorLabel,
  promotionLabel,
  promotionPercentage,
  promotionBackgroundColor,
  promotionTextColor,
  isOutlet,
  objectID,
  email,
  date,
  priceGiftCard,
  isGiftCard = false,
}: Props) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const image = getProductImage({
    productRef,
    productName,
    colorRef,
    colorLabel,
    position: 1,
    width: PRODUCT_IMAGE_WIDTH,
  });

  const [nameWithoutCategory, category] = productName?.split(' - ') ?? '';

  const onModalClose = () => {
    if (onClose) {
      onClose();
    }
    dispatch(setShowFeedback(false));
    dispatch(closeModalNew());
  };

  const onGoingToCartPage = () => {
    dispatch(setShowFeedback(false));
    dispatch(loadCart() as unknown as UnknownAction);
  };

  const isCartPage = pathname.startsWith(paths.CART);

  return (
    <StyledFeedBackContainer isGiftCard={isGiftCard}>
      <div className="feed-back-header-block">
        <div className="feed-back-icon-message-block">
          <Icon name="checkmarkCircle" size={16} color={colors.MAIN_SUCCESS} />
          <p>{MSG_FEEDBACK_SUCCESS}</p>
        </div>
        <button
          className="mobile-icon-close"
          type="button"
          aria-label="Bouton pour fermer la modale d'ajout avec succès au panier"
          onClick={onModalClose}
          data-testid="mobile-feed-back-close-button"
        >
          <Icon name="close" size={20} />
        </button>
      </div>
      <div className="feed-back-product-description-block">
        <img src={image?.jpg} alt={image?.alt} />
        <div className="feed-back-product-info-block">
          <div className="feed-back-product-name-price-block">
            <div className="feed-back-category-name">
              <p className="feed-back-category-price">
                {isGiftCard
                  ? productName
                  : (category ?? collectionName ?? 'categorie').toLocaleUpperCase()}
              </p>
              <span className="feed-back-store-price">
                {formatPrice(isGiftCard && priceGiftCard ? priceGiftCard : storePrice)}
              </span>
            </div>
            {!isGiftCard && (
              <div className="feed-back-category-name">
                <p className="feed-back-name">{nameWithoutCategory ?? 'nom du produit'}</p>
                {originalPrice > storePrice && (
                  <span className="feed-back-original-price">{formatPrice(originalPrice)}</span>
                )}
              </div>
            )}
          </div>
          <PromotionLabel
            promotionPercentage={promotionPercentage}
            promotionLabel={promotionLabel}
            promotionTextColor={promotionTextColor}
            promotionBackgroundColor={promotionBackgroundColor}
            isOutlet={isOutlet}
            objectID={objectID}
          />
          <div className="feed-back-product-details-block">
            <div className="feed-back-color-size-quantity-block">
              <p>{isGiftCard ? RECEIVER_EMAIL : LBL_COLOR}</p>
              <span>{isGiftCard ? email : colorLabel}</span>
            </div>
            <div className={`feed-back-color-size-quantity-block${isGiftCard ? '-gift-card' : ''}`}>
              <p>{isGiftCard ? DATE_OF_SEND : LBL_SIZE}</p>
              <span>{isGiftCard ? new Date(date ?? '').toLocaleDateString('fr-FR') : size}</span>
            </div>
            {!isGiftCard && (
              <div className="feed-back-color-size-quantity-block">
                <p>{LBL_QUANTITY}</p>
                <span>1</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isGiftCard && (
        <div className="feed-back-cross-sell-block">
          <Divider my="24px" />
          <CrossSell mainProductRef={productRef} mainProductColorRef={colorRef} />
        </div>
      )}
      <div className="feed-back-buttons-block">
        <button
          className={`${
            isCartPage ? 'feed-back-cart-page-close-button' : 'feed-back-close-button'
          }`}
          type="button"
          data-testid="feed-back-modal-close-button"
          aria-label="Bouton pour fermer la modale d'ajout avec succès au panier"
          onClick={onModalClose}
        >
          <p>{isCartPage ? MSG_RETURN_TO_BASKET : ERESA_CONTINUE_TO_BUY}</p>
        </button>
        {!isCartPage && (
          <Link
            className="feed-back-modal-link-to-cart-anchor"
            to={paths.CART}
            aria-label="lien vers le panier"
            data-testid="feed-back-modal-link-to-cart-page"
            onClick={onGoingToCartPage}
          >
            <p>{TXT_LINK_CART}</p>
          </Link>
        )}
      </div>
    </StyledFeedBackContainer>
  );
};
